import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Mountain, Check, X, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", screen_name: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snStatus, setSnStatus] = useState("idle"); // idle | checking | available | taken | short
  const debounceRef = useRef(null);

  useEffect(() => {
    const sn = form.screen_name.trim();
    setSnStatus("idle");
    if (!sn) return;
    if (sn.length < 2) { setSnStatus("short"); return; }
    setSnStatus("checking");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/check-screen-name", { params: { screen_name: sn } });
        setSnStatus(data.available ? "available" : (data.reason === "too_short" ? "short" : "taken"));
      } catch { setSnStatus("idle"); }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [form.screen_name]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (snStatus === "taken") { setError("That screen name is taken — pick another."); return; }
    setError(""); setLoading(true);
    try {
      let invited_by_screen_name = null;
      try { invited_by_screen_name = localStorage.getItem("mp_invited_by"); } catch { /* ignore */ }
      await register({ ...form, ...(invited_by_screen_name ? { invited_by_screen_name } : {}) });
      try { localStorage.removeItem("mp_invited_by"); } catch { /* ignore */ }
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const snBorder = snStatus === "taken" ? "border-[#8C3A3A]" : snStatus === "available" ? "border-[#3A5A40]" : "";

  return (
    <div className="paper-grain min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-16" data-testid="register-page">
      <div className="w-full max-w-md paper-card p-10">
        <div className="flex items-center gap-2 mb-6">
          <Mountain className="w-6 h-6" style={{ color: "var(--mp-terracotta)" }}/>
          <span className="font-script text-2xl text-[#5C5C5C]">a new chapter</span>
        </div>
        <h1 className="font-serif-display text-4xl mb-2">Claim your name.</h1>
        <p className="text-sm text-[#5C5C5C] mb-8">
          You can add hometown, schools and more later — only your screen name is searchable by default.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2 text-[#5C5C5C]">Screen name *</label>
            <div className="relative">
              <input data-testid="register-screen-name-input" required minLength={2} maxLength={40} className={`mp-input pr-10 ${snBorder}`}
                     placeholder="e.g. juneb_1998" value={form.screen_name} onChange={set("screen_name")} autoComplete="off"/>
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {snStatus === "checking" && <Loader2 className="w-4 h-4 text-[#8A8A8A] animate-spin"/>}
                {snStatus === "available" && <Check className="w-4 h-4 text-[#3A5A40]"/>}
                {snStatus === "taken" && <X className="w-4 h-4 text-[#8C3A3A]"/>}
              </span>
            </div>
            {snStatus === "available" && <p data-testid="sn-available" className="text-xs text-[#3A5A40] mt-1">@{form.screen_name.trim()} is available 🎉</p>}
            {snStatus === "taken" && <p data-testid="sn-taken" className="text-xs text-[#8C3A3A] mt-1">That name is already taken — try another.</p>}
            {snStatus === "short" && <p data-testid="sn-short" className="text-xs text-[#8A8A8A] mt-1">At least 2 characters.</p>}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2 text-[#5C5C5C]">Your name (optional)</label>
            <input data-testid="register-name-input" className="mp-input" value={form.name} onChange={set("name")}/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2 text-[#5C5C5C]">Email *</label>
            <input data-testid="register-email-input" type="email" required className="mp-input" value={form.email} onChange={set("email")}/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2 text-[#5C5C5C]">Password * (min 6)</label>
            <input data-testid="register-password-input" type="password" required minLength={6} className="mp-input" value={form.password} onChange={set("password")}/>
          </div>
          {error && <p data-testid="register-error" className="text-sm text-[#8C3A3A]">{error}</p>}
          <button data-testid="register-submit-btn" type="submit" disabled={loading || snStatus === "taken" || snStatus === "checking"} className="btn-primary w-full mt-6">
            {loading ? "..." : "Create my album"}
          </button>
        </form>
        <p className="text-sm text-[#5C5C5C] mt-6">
          Already here?{" "}
          <Link to="/login" className="text-[#8C3A3A] underline underline-offset-4" data-testid="register-to-login-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
